import { Injectable, inject } from '@angular/core';
import OneSignalPlugin from '@onesignal/capacitor-plugin';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from './analytics.service';

@Injectable({ providedIn: 'root' })
export class OneSignalService {
  private analytics = inject(AnalyticsService);

  private get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  async initialize(): Promise<void> {
    if (!this.isNative) return;
    try {
      OneSignalPlugin.initialize(environment.oneSignalAppId);
    } catch (err) {
      console.warn('[OneSignal] initialize failed', err);
    }
  }

  login(uid: string): void {
    if (!this.isNative) return;
    try {
      OneSignalPlugin.login(uid);
    } catch (err) {
      console.warn('[OneSignal] login failed', err);
    }
  }

  setUserTags(role: string): void {
    if (!this.isNative) return;
    try {
      OneSignalPlugin.User.addTag('role', role);
    } catch (err) {
      console.warn('[OneSignal] setUserTags failed', err);
    }
  }

  setCityTag(lat: number, lng: number, onCity?: (city: string) => void): void {
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then(r => r.json())
      .then((data: any) => {
        const city = data?.address?.city
          ?? data?.address?.town
          ?? data?.address?.municipality
          ?? data?.address?.county;
        if (city) {
          const cityKey = city.toLowerCase();
          try { localStorage.setItem('happeningph_user_city', cityKey); } catch (_) {}
          if (this.isNative) OneSignalPlugin.User.addTag('city', cityKey);
          this.analytics.setUserProperty('city', cityKey);
          onCity?.(cityKey);
        }
      })
      .catch(() => { /* silently skip if reverse geocode fails */ });
  }

  async addEmail(email: string): Promise<void> {
    if (!this.isNative) return;
    try {
      await OneSignalPlugin.User.addEmail(email);
    } catch (err) {
      console.warn('[OneSignal] addEmail failed', err);
    }
  }

  triggerLoggedIn(): void {
    if (!this.isNative) return;
    try {
      OneSignalPlugin.InAppMessages.addTrigger('logged_in', '1');
    } catch (err) {
      console.warn('[OneSignal] triggerLoggedIn failed', err);
    }
  }

  // ── Featured happenings triggers ──────────────────────────────────────────

  /** Call once per app session (cold start / foreground resume). */
  trackAppOpen(): void {
    if (!this.isNative) return;
    try {
      const count = (parseInt(localStorage.getItem('os_app_opens') ?? '0', 10) || 0) + 1;
      localStorage.setItem('os_app_opens', String(count));
      OneSignalPlugin.InAppMessages.addTrigger('app_opens', String(count));
    } catch (err) {
      console.warn('[OneSignal] trackAppOpen failed', err);
    }
  }

  /** Call each time a happening detail page is opened. */
  trackHappeningViewed(): void {
    if (!this.isNative) return;
    try {
      const count = (parseInt(sessionStorage.getItem('os_hap_viewed') ?? '0', 10) || 0) + 1;
      sessionStorage.setItem('os_hap_viewed', String(count));
      OneSignalPlugin.InAppMessages.addTrigger('happenings_viewed', String(count));
    } catch (err) {
      console.warn('[OneSignal] trackHappeningViewed failed', err);
    }
  }

  /**
   * Call when the app comes back to foreground.
   * Sets `no_booking_session = "1"` if the user viewed happenings last
   * session but never reserved. Clears itself after being set so it only
   * fires once per eligible gap.
   */
  checkNoBookingSession(): void {
    if (!this.isNative) return;
    try {
      const viewed  = parseInt(localStorage.getItem('os_prev_viewed') ?? '0', 10) || 0;
      const booked  = parseInt(localStorage.getItem('os_prev_booked') ?? '0', 10) || 0;
      if (viewed >= 2 && booked === 0) {
        OneSignalPlugin.InAppMessages.addTrigger('no_booking_session', '1');
        localStorage.removeItem('os_prev_viewed');
        localStorage.removeItem('os_prev_booked');
      }
    } catch (err) {
      console.warn('[OneSignal] checkNoBookingSession failed', err);
    }
  }

  /** Call after a reservation is successfully created. */
  trackBookingMade(): void {
    if (!this.isNative) return;
    try {
      const count = (parseInt(localStorage.getItem('os_prev_booked') ?? '0', 10) || 0) + 1;
      localStorage.setItem('os_prev_booked', String(count));
    } catch (err) {
      console.warn('[OneSignal] trackBookingMade failed', err);
    }
  }

  /** Snapshot the session's happenings_viewed count to localStorage on app background. */
  snapshotSessionOnBackground(): void {
    if (!this.isNative) return;
    try {
      const viewed = sessionStorage.getItem('os_hap_viewed') ?? '0';
      localStorage.setItem('os_prev_viewed', viewed);
    } catch {
      // silently skip — sessionStorage may be unavailable
    }
  }

  async optOutPush(): Promise<void> {
    if (!this.isNative) return;
    try {
      await OneSignalPlugin.User.pushSubscription.optOut();
    } catch (err) {
      console.warn('[OneSignal] optOut failed', err);
    }
  }

  async optInPush(): Promise<void> {
    if (!this.isNative) return;
    try {
      await OneSignalPlugin.User.pushSubscription.optIn();
    } catch (err) {
      console.warn('[OneSignal] optIn failed', err);
    }
  }

  async isPushOptedIn(): Promise<boolean> {
    if (!this.isNative) return false;
    try {
      return await OneSignalPlugin.User.pushSubscription.getOptedInAsync();
    } catch {
      return false;
    }
  }

  /** Request OS-level notification permission and opt in to push. Call after signup on native. */
  async requestAndOptIn(): Promise<void> {
    if (!this.isNative) return;
    try {
      await OneSignalPlugin.Notifications.requestPermission(true);
      await OneSignalPlugin.User.pushSubscription.optIn();
    } catch (err) {
      console.warn('[OneSignal] requestAndOptIn failed', err);
    }
  }

  logout(): void {
    if (!this.isNative) return;
    try {
      OneSignalPlugin.logout();
    } catch (err) {
      console.warn('[OneSignal] logout failed', err);
    }
  }
}
